import { useEffect, type RefObject } from "react";

/**
 * Closes a popover when the user clicks outside the referenced element OR
 * presses Escape. Used by the Mail UI popovers (LabelPicker, SnoozeMenu,
 * TemplatePicker, etc.) so they share the same dismissal model.
 *
 * Pass `enabled = false` to detach the listeners (e.g. when the popover is
 * already closed) — saves us a re-render cycle's worth of global handlers.
 */
export function useDismiss(
    ref: RefObject<HTMLElement | null>,
    onDismiss: () => void,
    enabled = true
) {
    useEffect(() => {
        if (!enabled) return;
        const onPointer = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onDismiss();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onDismiss();
        };
        document.addEventListener("mousedown", onPointer);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onPointer);
            document.removeEventListener("keydown", onKey);
        };
    }, [enabled, onDismiss, ref]);
}
